import React, {
  useState,
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
  useCallback,
} from "react";

import { useAvailableCountries } from "lib/config";

import { WebMercatorViewport } from "@deck.gl/core";

import BaseMap from "./BaseMap";
import MapLoading from "components/MapLoading";
import ChartTooltip from "components/ChartTooltip";

import {
  getCountryChoroplethLayers,
  getStateChoroplethLayers,
  getWorldChoroplethLayers,
  getCountyChoroplethLayers,
} from "./layers/choropleth";

import { getPlacesLayer } from "./layers/places";

import { getStateHexagonLayer } from "./layers/hexagons";

function getBoundsForPlaces(params, places) {
  if (!places || places.length === 0) {
    return params;
  }

  let minLat = +Infinity;
  let maxLat = -Infinity;
  let minLon = +Infinity;
  let maxLon = -Infinity;

  for (let place of places) {
    const lat = place.geometry.location.lat();
    const lon = place.geometry.location.lng();

    minLat = Math.min(lat, minLat);
    maxLat = Math.max(lat, maxLat);
    minLon = Math.min(lon, minLon);
    maxLon = Math.max(lon, maxLon);
  }

  // [minX, minY, maxX, maxY]
  const result = [minLon, minLat, maxLon, maxLat];
  return { initialBbox: result };
}

export const mapTypes = {
  worldChoropleth: 1,
  countryChoropleth: 2,
  stateChoropleth: 3,
  countyChoropleth: 4,
  stateHexagons: 5,
};

function createLayers(
  type,
  data,
  geoJson,
  setTooltip,
  onPolygonClick,
  availableCountries
) {
  if (process.env.NODE_ENV === "development") {
    console.log("MobilityMap: createLayers");
  }

  if (!data) {
    return {};
  }

  if (type === mapTypes.countryChoropleth) {
    if (!geoJson) {
      return {};
    } else {
      return getCountryChoroplethLayers({
        data: data?.content,
        geoJson,
        onPolygonHover: setTooltip,
        onPolygonClick,
      });
    }
  } else if (type === mapTypes.stateHexagons) {
    return getStateHexagonLayer({
      data: data?.content,
    });
  } else if (type === mapTypes.countyChoropleth) {
    if (!geoJson) {
      return {};
    } else {
    }
    return getCountyChoroplethLayers({
      data: data?.content,
      geoJson,
      onPolygonHover: setTooltip,
    });
  } else if (type === mapTypes.stateChoropleth) {
    if (!geoJson) {
      return {};
    } else {
    }
    return getStateChoroplethLayers({
      data: data?.content,
      geoJson,
      onPolygonHover: setTooltip,
      onPolygonClick,
    });
  } else if (type === mapTypes.worldChoropleth) {
    if (!geoJson) {
      return {};
    } else {
    }
    return getWorldChoroplethLayers({
      data: data?.content,
      geoJson,
      onPolygonHover: setTooltip,
      onPolygonClick,
      availableCountries,
    });
  } else {
    throw new Error(`Map type ${type} not supported,`);
  }
}

// Component
function MobilityMap(
  {
    type,
    data,
    geoJson,
    initialBbox,
    initialLatitude,
    initialLongitude,
    initialZoom,
    onPolygonClick,
    onBoundsChange,
    searchPlaces,
  },
  ref
) {
  const [tooltip, setTooltip] = useState(null);

  // TODO: Check if mounted. It should be done in create layers.
  // The problem is that setTooltip may be called after the componente is unmounted.
  // Must find a better way to solve this.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleTooltipHover = useCallback(
    (...args) => {
      if (mounted.current) {
        setTooltip(...args);
      }
    },
    [setTooltip]
  );

  //

  useEffect(() => {
    if (type === mapTypes.stateHexagons) {
      setTooltip(null);
    }
  }, [type]);

  const availableCountries = useAvailableCountries();

  const { layers, colorScale } = useMemo(
    () =>
      createLayers(
        type,
        data,
        geoJson,
        handleTooltipHover,
        onPolygonClick,
        availableCountries
      ),
    [
      type,
      data,
      geoJson,
      handleTooltipHover,
      onPolygonClick,
      availableCountries,
    ]
  );

  const placesLayer = useMemo(() => getPlacesLayer(searchPlaces), [
    searchPlaces,
  ]);

  const nextMapPosition = useMemo(
    () =>
      getBoundsForPlaces(
        { initialBbox, initialLatitude, initialLongitude, initialZoom },
        searchPlaces
      ),
    [initialBbox, initialLatitude, initialLongitude, initialZoom, searchPlaces]
  );

  const fullLayers = useMemo(
    () => (layers ? layers.concat(placesLayer) : placesLayer),
    [layers, placesLayer]
  );

  if (process.env.NODE_ENV === "development") {
    console.log("Rendering MobilityMap: ");
  }

  useImperativeHandle(
    ref,
    () => {
      return {
        colorScale,
      };
    },
    [colorScale]
  );

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <BaseMap
        {...nextMapPosition}
        layers={fullLayers}
        onBoundsChange={onBoundsChange}
      />

      {tooltip ? <ChartTooltip {...tooltip} /> : <div />}
      {fullLayers == null ? (
        <div
          style={{
            position: "absolute",
            pointerEvents: "none",
            zIndex: 10,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <MapLoading />
        </div>
      ) : null}
    </div>
  );
}

export default React.memo(forwardRef(MobilityMap));

if (process.env.NODE_ENV === "development") {
  MobilityMap.whyDidYouRender = {
    logOnDifferentValues: true,
  };
}
